import { Vendor } from '../classes/models';
import { UserAccountRoles } from '../interfaces/user-account-roles';
import { ConfirmModalComponent } from '../modals/confirm-modal/confirm-modal.component';

// The backend (vive-v2 vendor-name-length.ts) applies the same limit, the same
// code-point count and the same sentence — keep the three in step.
const VENDOR_NAME_MAX_LENGTH = 80;

/**
 * The OK-only ConfirmModal inputs to show when a vendor's legal name or DBA is
 * over the limit, or null when both fit. Run it in the save handler, not as a
 * form validator: a validator would surface only as the generic "error with a
 * field" toast, and a maxLength on the input would stop typing at the limit so
 * the entered length could never be shown.
 */
export function vendorNameLengthModalInputs(
  fields: { legalName?: string | null; dba?: string | null },
  { legalNameLockedForUser = false }: { legalNameLockedForUser?: boolean } = {}
): Partial<ConfirmModalComponent> | null {
  const over = (
    [
      ['Legal name', fields.legalName],
      ['DBA', fields.dba]
    ] as const
  )
    .map(([label, value]) => ({
      label,
      length: value ? [...value].length : 0
    }))
    .filter(({ length }) => length > VENDOR_NAME_MAX_LENGTH);

  if (over.length === 0) {
    return null;
  }

  const legalNameOver = over.some(({ label }) => label === 'Legal name');

  return {
    title:
      over.length > 1
        ? 'Legal name and DBA are too long'
        : `${over[0].label} is too long`,
    // ConfirmModal renders displayText with white-space: pre-wrap.
    displayText: over
      .map(
        ({ label, length }) =>
          `${label} cannot exceed ${VENDOR_NAME_MAX_LENGTH} characters. This entry is ${length} characters. Please shorten it and try again.`
      )
      .join('\n'),
    ...(legalNameOver &&
      legalNameLockedForUser && {
        consequenceText:
          "Only an administrator can change this vendor's legal name."
      }),
    hideCancelButton: true,
    continueButtonText: 'OK'
  };
}

/**
 * A vendor that has signed and works with several clients keeps its legal name
 * unless an admin or the fraud team changes it. Same rule as
 * VendorEditComponent.handleTaxIdOrLegalNameClick — change both together.
 */
export function isLegalNameLockedForUser(
  vendor:
    | Pick<Vendor, 'hasSignedAnyVendorAgreement' | 'hasMultipleClients'>
    | undefined,
  role: UserAccountRoles | null | undefined
): boolean {
  return (
    !!vendor?.hasSignedAnyVendorAgreement &&
    !!vendor.hasMultipleClients &&
    !(role === 'ADMIN_ADMIN' || role === 'ADMIN_FRAUD_TEAM')
  );
}
